import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { myAssignmentsApi, type MyAssignmentStats } from './api'
import { useAuthStore } from '@/app/store/auth'
import type { ListParams } from '@/lib/api/types'
import { useMemo } from 'react'

export const myAssignmentKeys = {
    all: ['my-assignments'] as const,
    lists: () => [...myAssignmentKeys.all, 'list'] as const,
    list: (params: ListParams) => [...myAssignmentKeys.lists(), params] as const,
}

export function useMyAssignments(params?: ListParams & { requestID?: string; location?: string; team?: string; recoverySet?: string }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: myAssignmentKeys.list(params || {}),
        queryFn: () => myAssignmentsApi.list(params),
        enabled: isAuthenticated && !mfaRequired,
        placeholderData: keepPreviousData,
    })
}

/**
 * Hooks that derive stats and filter options from the main list response.
 * No separate API calls needed — the backend embeds them in the list response.
 */

export function useMyAssignmentStats(listResponse: any) {
    return useMemo<MyAssignmentStats>(() => {
        const stats = listResponse?.stats
        return {
            uniqueRequestCount: stats?.uniqueRequestCount ?? 0,
            objectCount: stats?.objectCount ?? 0,
            locationCount: stats?.locationCount ?? 0,
        }
    }, [listResponse?.stats])
}

export function useMyAssignmentLocationOptions(listResponse: any) {
    return useMemo<string[]>(() => {
        return listResponse?.filterOptions?.locations || []
    }, [listResponse?.filterOptions?.locations])
}

export function useMyAssignmentTeamOptions(listResponse: any) {
    return useMemo<string[]>(() => {
        return listResponse?.filterOptions?.teams || []
    }, [listResponse?.filterOptions?.teams])
}

export function useMyAssignmentRecoverySetOptions() {
    // Recovery sets aren't in the view — return empty
    return useMemo<string[]>(() => [], [])
}
